import { useEffect, useState, useMemo } from 'react'
import { Typography } from '@fullscreendigital/carrefour-dex-fe'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { ErrorBoundary } from 'components/core-ui'
import { DrawerContext } from 'contexts'
import { DRAWER_WIDTH } from 'utils/constants'
import { useAuth } from 'oidc-react'
import s from './Layout.scss'
import { setToken, isAuthenticated, getUserRoles, logout } from 'utils/authService'
import { Notification } from './Notification'
import { FirebaseNotification } from 'components/core-ui/FirebaseNotification'
import { OfflineTaskManager, useOfflineTool } from '../core-ui/Offline'
import { makeCorePostRequest, makeCorePutRequest } from '../../utils/api'
import {
  getShopReceptionShops,
  getShopReceptionHub,
  getShopReceptionWarehouse,
  getWMSNoDailyReportsMessage
} from './api'
import { isAllowed } from 'utils/helpers'
import {
  SHOP_RECEPTION_FETCH_SHOP_ROLES,
  SHOP_RECEPTION_FETCH_HUB_ROLES,
  SHOP_RECEPTION_FETCH_WAREHOUSE_ROLES,
  HUB,
  WAREHOUSE,
  SHOP,
  WMS_FETCH_MISSING_DAILY_REPORTS_ROLES
} from './constants'
import { useNotification } from 'hooks'
import { ShopReceptionNoAssociationModal } from './ShopReceptionNoAssociationModal'
import { WasteManagementNoDailyReportsModal } from './WasteManagementNoDailyReportsModal'
import dayjs from 'dayjs'

const Layout = ({ children, modules, setSubdomainId }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH)
  const [isWMSNoDailyReportsModalOpen, setIsWMSNoDailyReportsModalOpen] = useState(false)
  const [showNoAssocModal, setShowNoAssocModal] = useState(false)

  const openWMSNoDailyReportsModal = () => {
    setIsWMSNoDailyReportsModalOpen(true)
  }

  const closeWMSNoDailyReportsModal = () => {
    setIsWMSNoDailyReportsModalOpen(false)
  }

  const auth = useAuth()

  const offlineTool = useOfflineTool()

  const { showNotification } = useNotification()

  useEffect(() => {
    if (noAssociationModalInfo?.display) setShowNoAssocModal(true)
  }, [noAssociationModalInfo?.display])

  useEffect(() => {
    setDrawerWidth(isMenuOpen ? DRAWER_WIDTH : 0)
  }, [isMenuOpen])

  useEffect(() => {
    if (auth?.userData) {
      setToken(auth.userData.id_token)
      console.log('new token set!!!!!!!!!!!!')
    }
  }, [auth.userData])

  const currentUserRoles = useMemo(() => getUserRoles(), [])

  const { data: noDailyReportsInfo } = getWMSNoDailyReportsMessage(
    showNotification,
    currentUserRoles && isAllowed(WMS_FETCH_MISSING_DAILY_REPORTS_ROLES, currentUserRoles)
  )

  useEffect(() => {
    if (currentUserRoles && isAllowed(WMS_FETCH_MISSING_DAILY_REPORTS_ROLES, currentUserRoles)) {
      const wasteManagementNoDailyReportsModalVisible = localStorage.getItem(
        'wasteManagementNoDailyReportsModalVisible'
      )

      if (
        wasteManagementNoDailyReportsModalVisible !== dayjs().format('YYYY-MM-DD') &&
        noDailyReportsInfo?.length
      ) {
        openWMSNoDailyReportsModal()
        localStorage.removeItem('wasteManagementNoDailyReportsModalVisible')
      }
    }
  }, [noDailyReportsInfo, currentUserRoles])

  const { data: shops } = getShopReceptionShops(
    showNotification,
    currentUserRoles && isAllowed(SHOP_RECEPTION_FETCH_SHOP_ROLES, currentUserRoles)
  )

  const { data: hub } = getShopReceptionHub(
    showNotification,
    currentUserRoles && isAllowed(SHOP_RECEPTION_FETCH_HUB_ROLES, currentUserRoles)
  )

  const { data: warehouse } = getShopReceptionWarehouse(
    showNotification,
    currentUserRoles && isAllowed(SHOP_RECEPTION_FETCH_WAREHOUSE_ROLES, currentUserRoles)
  )

  const noAssociationModalInfo = useMemo(() => {
    if (currentUserRoles) {
      if (isAllowed(SHOP_RECEPTION_FETCH_SHOP_ROLES, currentUserRoles)) {
        return shops?.length === 0 ? { type: SHOP, display: true } : { display: false }
      } else if (isAllowed(SHOP_RECEPTION_FETCH_HUB_ROLES, currentUserRoles)) {
        return hub === 'HUB Neidentificat' ? { type: HUB, display: true } : { display: false }
      } else if (isAllowed(SHOP_RECEPTION_FETCH_WAREHOUSE_ROLES, currentUserRoles)) {
        return warehouse === 'DEPOZIT Neidentificat'
          ? { type: WAREHOUSE, display: true }
          : { display: false }
      } else {
        return { display: false }
      }
    }
  }, [currentUserRoles, shops, hub, warehouse])

  const handleOpenMenu = () => {
    setIsMenuOpen(true)
  }

  const handleCloseMenu = () => {
    setIsMenuOpen(false)
  }

  const handleLogout = async () => {
    logout()
    offlineTool.deleteOwnTasks()
    auth.signOutRedirect()
  }

  return (
    <DrawerContext.Provider
      value={{
        drawerWidth,
        isMenuOpen
      }}
    >
      <Typography variant="light" component="div">
        <div>
          <Notification />
          <FirebaseNotification />
          <Header handleOpenMenu={handleOpenMenu} />
          {isAuthenticated() && (
            <Sidebar
              isMenuOpen={isMenuOpen}
              handleCloseMenu={handleCloseMenu}
              modules={modules}
              setSubdomainId={setSubdomainId}
              handleLogout={handleLogout}
            />
          )}
          <div className={s.contentWrapper}>
            <ErrorBoundary>
              <OfflineTaskManager
                requestHandlers={{ post: makeCorePostRequest, put: makeCorePutRequest }}
              >
                {noAssociationModalInfo?.display && (
                  <ShopReceptionNoAssociationModal
                    type={noAssociationModalInfo.type}
                    isOpen={showNoAssocModal}
                    onClose={() => setShowNoAssocModal(false)}
                  />
                )}
                {!!noDailyReportsInfo?.length && (
                  <WasteManagementNoDailyReportsModal
                    isModalOpen={isWMSNoDailyReportsModalOpen}
                    closeModal={closeWMSNoDailyReportsModal}
                    info={noDailyReportsInfo}
                  />
                )}
                {children}
              </OfflineTaskManager>
            </ErrorBoundary>
          </div>
        </div>
      </Typography>
    </DrawerContext.Provider>
  )
}

export default Layout
